import CurrencyInput from "../components/CurrencyInput";
import { MarketStatistic } from "../components/MarketStatistic";
import { PlannedExpensesList } from "../components/PlannedExpensesList";
import { SectionHeader } from "../components/SectionHeader";
import { ShowMoreToggle } from "../components/ShowMoreToggle";
import { useExpensesLogic } from "./Expenses.logic";
import { useFieldStatistic } from "../hooks/useStatistics";
import { useStatisticsValue } from "../hooks/useStatisticsValue";

/**
 * ExpensesForm - Pure form component (no layout)
 * Used by both ExpensesSettings.mobile (with SettingsPageLayout) and SettingsModal
 *
 * Essential: housing, utilities, food, transportation, insurance, healthcare,
 *            other, all debt payments
 * Advanced: personalDiscretionary
 */
export function ExpensesForm() {
  const {
    formData,
    handleChange,
    handleBlur,
    loading,
    totalExpenses,
    showAdvanced,
    toggleAdvanced,
    plannedExpenses,
    handleAddPlannedExpense,
    handleRemovePlannedExpense,
    handlePlannedExpenseChange,
  } = useExpensesLogic();

  // Get age-specific statistic value
  const expensesStatistic = useStatisticsValue("expenses");

  // Get statistics help text for each field
  const housingHelp = useFieldStatistic(
    "housing",
    "Rent, mortgage payment, property tax, insurance"
  );
  const utilitiesHelp = useFieldStatistic(
    "utilities",
    "Electricity, gas, water, internet, phone"
  );
  const foodHelp = useFieldStatistic("food", "Food and groceries");
  const transportationHelp = useFieldStatistic(
    "transportation",
    "Gas, transit, parking, vehicle maintenance"
  );
  const insuranceHelp = useFieldStatistic(
    "insurance",
    "Home, auto, life insurance premiums"
  );
  const healthcareHelp = useFieldStatistic(
    "healthcare",
    "Prescriptions, dental, vision, medical expenses"
  );
  const mortgagePaymentHelp = useFieldStatistic(
    "mortgagePayment",
    "Monthly mortgage payment (principal + interest)"
  );
  const creditCardPaymentHelp = useFieldStatistic(
    "creditCardPayment",
    "Monthly credit card payment"
  );
  const carLoanPaymentHelp = useFieldStatistic(
    "carLoanPayment",
    "Monthly vehicle loan or lease payment"
  );
  const lineOfCreditPaymentHelp = useFieldStatistic(
    "lineOfCreditPayment",
    "Monthly line of credit payment"
  );
  const studentLoanPaymentHelp = useFieldStatistic(
    "studentLoanPayment",
    "Monthly student loan payment"
  );
  const otherLiabilitiesPaymentHelp = useFieldStatistic(
    "otherLiabilitiesPayment",
    "Monthly payments for other debts"
  );
  const otherHelp = useFieldStatistic(
    "other",
    "Entertainment, clothing, personal care, etc."
  );

  // Advanced field statistics
  const personalDiscretionaryHelp = useFieldStatistic(
    "personalDiscretionary",
    "Dining out, hobbies, subscriptions, personal purchases"
  );

  if (loading) {
    return null;
  }

  return (
    <div className="settings-form-container" id="expenses-form">
      <form className="settings-form-scroll">
        {/* ── Living Expenses ── */}
        <SectionHeader title="Living Expenses" sectionId="living-expenses" />

        <div className="form-grid">
          <CurrencyInput
            name="housing"
            value={formData.housing}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Housing"
            helpText={housingHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="utilities"
            value={formData.utilities}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Utilities"
            helpText={utilitiesHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="food"
            value={formData.food}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Food & Groceries"
            helpText={foodHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="transportation"
            value={formData.transportation}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Transportation"
            helpText={transportationHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="insurance"
            value={formData.insurance}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Insurance"
            helpText={insuranceHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="healthcare"
            value={formData.healthcare}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Healthcare"
            helpText={healthcareHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="other"
            value={formData.other}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Other Expenses"
            helpText={otherHelp}
            suffix="/mo"
          />
        </div>

        {/* ── Debt Payments ── */}
        <SectionHeader title="Debt Payments" sectionId="debt-payments" />

        <div className="form-grid">
          <CurrencyInput
            name="mortgagePayment"
            value={formData.mortgagePayment}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Mortgage Payment"
            helpText={mortgagePaymentHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="creditCardPayment"
            value={formData.creditCardPayment}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Credit Card Payment"
            helpText={creditCardPaymentHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="carLoanPayment"
            value={formData.carLoanPayment}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Car Loan Payment"
            helpText={carLoanPaymentHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="lineOfCreditPayment"
            value={formData.lineOfCreditPayment}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Line of Credit Payment"
            helpText={lineOfCreditPaymentHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="studentLoanPayment"
            value={formData.studentLoanPayment}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Student Loan Payment"
            helpText={studentLoanPaymentHelp}
            suffix="/mo"
          />

          <CurrencyInput
            name="otherLiabilitiesPayment"
            value={formData.otherLiabilitiesPayment}
            onChange={handleChange}
            onBlur={handleBlur}
            label="Other Liabilities Payment"
            helpText={otherLiabilitiesPaymentHelp}
            suffix="/mo"
          />
        </div>

        {/* ── Advanced Fields ── */}
        <ShowMoreToggle expanded={showAdvanced} onToggle={toggleAdvanced}>
          <SectionHeader
            title="Discretionary Spending"
            sectionId="discretionary-spending"
          />

          <div className="form-grid">
            <CurrencyInput
              name="personalDiscretionary"
              value={formData.personalDiscretionary}
              onChange={handleChange}
              onBlur={handleBlur}
              label="Personal Discretionary"
              helpText={personalDiscretionaryHelp}
              suffix="/mo"
            />
          </div>

          <SectionHeader
            title="Planned Large Expenses"
            sectionId="planned-expenses"
          />

          <PlannedExpensesList
            expenses={plannedExpenses}
            onAdd={handleAddPlannedExpense}
            onRemove={handleRemovePlannedExpense}
            onChange={handlePlannedExpenseChange}
          />
        </ShowMoreToggle>
      </form>

      {/* Total (calculated, read-only) - Sticky at bottom for long forms */}
      <div className="form-sticky-save" id="expenses-total-bar">
        {/* Market Statistic Information - Between Total label and horizontal rule */}
        <div className="form-total-statistic-spacing">
          <MarketStatistic
            label="Canadian Monthly Expenses Statistic"
            marketAverage={expensesStatistic}
            currency={true}
          />
        </div>

        <div className="form-page-total-row">
          <span className="form-page-total-label">Total Monthly Expenses</span>
          <span id="total" className="form-page-total-value-blue">
            ${totalExpenses.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
